import { Button, Flex, Text, Tabs, TabList } from "@chakra-ui/react";
import { CSSProperties } from "react";
import { BrowseTabType } from "../../../types";
import { UnderlinedTab } from "../../../components/tabs/UnderlinedTab";
import { useMobileBreakpoint } from "../../../utils/useBreakpoints";

interface BrowseTabsProps {
  setActive: (index: BrowseTabType) => void;
  active: BrowseTabType;
  activeCount?: number | null;
}

type VisibleTab = {
  type: BrowseTabType;
  label: string;
};

const TABS: VisibleTab[] = [
  { type: BrowseTabType.comics, label: "Series" },
  { type: BrowseTabType.releases, label: "Releases" },
  { type: BrowseTabType.creators, label: "Creators" },
  { type: BrowseTabType.bundles, label: "Bundles" },
];

export function FloatingBrowseTab({ setActive, active }: BrowseTabsProps) {
  return (
    <Flex
      borderRadius="20px"
      background="charcoal.charcoal"
      backdropFilter="blur(18px)"
      border="1px solid"
      borderColor="transparent.white.10"
      boxShadow="0px 4px 24px 0px rgba(0, 0, 0, 0.40)"
      width={TABS.length >= 4 ? "330px" : "265px"}
      height="48px"
      justifyContent="space-evenly"
      alignItems="center"
    >
      {TABS.map((tab) => (
        <BrowseTab
          key={tab.type}
          text={tab.label}
          onClick={() => setActive(tab.type)}
          isActive={active === tab.type}
          floating
          fontSize="16px"
          fontWeight={700}
          fontFamily="Bricolage Grotesque"
        />
      ))}
    </Flex>
  );
}

function formatCount(count: number): string {
  if (count >= 1000) return "1k+";
  return String(count);
}

export function BrowseTabs({ setActive, active, activeCount }: BrowseTabsProps) {
  const visualIndex = TABS.findIndex((t) => t.type === active);
  const isMobile = useMobileBreakpoint();

  return (
    <Tabs
      variant="unstyled"
      colorScheme="blaze"
      index={visualIndex >= 0 ? visualIndex : 0}
      onChange={(index) => {
        const tab = TABS[index];
        if (tab) setActive(tab.type);
      }}
    >
      <TabList>
        {TABS.map((tab) => {
          const count = tab.type === active && activeCount != null && activeCount > 0
            ? activeCount
            : undefined;
          return (
            <UnderlinedTab
              key={tab.type}
              whiteSpace="nowrap"
              fontSize={isMobile ? "14px" : "20px"}
              paddingBottom={isMobile ? "8px" : "12px"}
              marginTop={isMobile ? "4px" : "8px"}
            >
              {count !== undefined
                ? `${tab.label} (${formatCount(count)})`
                : tab.label}
            </UnderlinedTab>
          );
        })}
      </TabList>
    </Tabs>
  );
}

interface BrowseTabProps {
  text: string;
  isActive: boolean;
  floating?: boolean;
  onClick: () => void;
  fontSize?: CSSProperties["fontSize"];
  fontWeight?: CSSProperties["fontWeight"];
  fontFamily?: CSSProperties["fontFamily"];
}
function BrowseTab({
  text,
  isActive,
  onClick,
  floating,
  fontSize,
  fontWeight,
  fontFamily,
}: BrowseTabProps) {
  return (
    <Button
      variant={floating ? "browseTab" : undefined}
      key={text}
      transition="all .2s linear"
      cursor="pointer"
      onClick={onClick}
      // px="36px"
      height="40px"
      alignItems="center"
      justifyContent="center"
      borderRadius="16px"
      bg={
        floating ? undefined : isActive ? "blaze.600" : "transparent.white.10"
      }
      color={"neutral.white"}
      _hover={{
        color: isActive ? "neutral.white" : "dune.100",
        background: isActive ? undefined : "transparent.white.20",
      }}
      marginInline="4px"
    >
      <Text
        transition="all .2s linear"
        fontFamily={fontFamily}
        fontSize={fontSize ?? "14px"}
        fontWeight={fontWeight}
        lineHeight="130%"
        color={floating ? (isActive ? "dune.100" : "dune.700") : "white"}
        _groupHover={{ color: "dune.100" }}
        marginBottom="2px"
      >
        {text}
      </Text>
    </Button>
  );
}
